/* Slot D5 — shared preorder schedule helpers (frontend-only).
 *
 * The backend rejects scheduled_for that is less than 15 minutes in the
 * future. We mirror that lead time here for an inline UI guard so users
 * never *submit* an obviously invalid time. The backend remains the
 * source of truth — any UI race that slips through still gets a 400.
 *
 * Format conventions:
 *   - `<input type="datetime-local">` works in LOCAL time, no TZ.
 *     min/value strings look like "2026-02-19T11:30" (no seconds, no Z).
 *   - On submit we convert that local string into a proper ISO-8601
 *     string with offset via `new Date(local).toISOString()` so the
 *     backend can parse it deterministically (UTC normalised).
 */

const MIN_LEAD_MS = 15 * 60 * 1000;
const STEP_MS = 5 * 60 * 1000;

/** Returns YYYY-MM-DDTHH:MM in local time, rounded UP to the next
 *  5-minute increment, plus a 15-minute lead. Use as the `min` and
 *  initial value of a <input type="datetime-local"> picker. */
export function minLocalScheduleString(now = new Date()) {
  const t = now.getTime() + MIN_LEAD_MS;
  const rounded = new Date(Math.ceil(t / STEP_MS) * STEP_MS);
  // Build YYYY-MM-DDTHH:MM in local time (no UTC conversion).
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${rounded.getFullYear()}-${pad(rounded.getMonth() + 1)}-${pad(rounded.getDate())}` +
    `T${pad(rounded.getHours())}:${pad(rounded.getMinutes())}`
  );
}

/** Convert a datetime-local string ("YYYY-MM-DDTHH:MM") into an ISO-8601
 *  UTC string the backend can parse. Returns null on empty/invalid input.
 *
 *  Contract: the input is interpreted in the operator's LOCAL browser
 *  timezone (which is exactly what the native <input type="datetime-local">
 *  emits). `new Date(local)` parses that as local; `.toISOString()`
 *  serialises the same instant as UTC. Daylight-saving is honoured
 *  automatically by the browser — never hardcode AEDT/AEST/UTC+10/+11. */
export function localToISO(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** iter_DELIVERY_EDIT_BUTTON_UPDATE_FIX (2026-02-28) — Canonical inverse of
 *  `localToISO`. Convert an offset-aware UTC/ISO-8601 datetime string
 *  into the "YYYY-MM-DDTHH:MM" local-time string that
 *  `<input type="datetime-local">` requires as `value`.
 *
 *  Reads Date's *local* getters — this is the fix for the previous
 *  `.slice(0, 16)` bug that showed UTC hours as if they were local
 *  (drifting the picker by the browser offset on every open/save).
 *
 *  Round-trip invariant: `localToISO(isoToLocalInputValue(iso)) === iso`
 *  for any minute-precision UTC ISO — the picker is minute-precision, so
 *  seconds/millis are truncated on the round trip but the calendar day
 *  and clock time (including AM/PM) survive.
 *
 *  Returns "" on empty/invalid input (safe for direct use in the
 *  `value` prop). */
export function isoToLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** True if the local string is at least the 15-minute lead in the future. */
export function isLocalTimeValid(local, now = new Date()) {
  if (!local) return false;
  const d = new Date(local);
  if (isNaN(d.getTime())) return false;
  return d.getTime() >= now.getTime() + MIN_LEAD_MS;
}

/** Short chip-ready local label, e.g. "Wed 3:30 PM" — for KDS badges +
 *  customer summary lines. */
export function fmtScheduledChip(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    weekday: "short", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

/** iter_122 (2026-06-12) — Australian-format confirmation label rendered
 *  under the preorder picker on the customer checkout. Returns
 *  "DD/MM/YYYY h:mma" (e.g. "06/06/2026 6:00pm"). Empty on invalid input.
 *  Used by CustomerOrder.jsx so the user has an unambiguous read of
 *  their selection even when the native picker's locale differs. */
export function fmtScheduledAU(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const h24 = d.getHours();
  const h12 = h24 % 12 || 12;
  const min = pad(d.getMinutes());
  const ampm = h24 < 12 ? "am" : "pm";
  return `${dd}/${mm}/${yyyy} ${h12}:${min}${ampm}`;
}
